import { Link } from 'react-router';
import { Building2, HeartHandshake, Hospital, ShieldCheck, Sparkles, UserRoundCog, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

const solutions = [
  {
    icon: Users,
    title: 'People & HR teams',
    useCase: 'Lower avoidable attrition and attendance drag by surfacing early relationship-risk signals before they become formal performance issues.',
    outcomes: [
      'Anonymized relationship wellness trend visibility by cohort',
      'Prioritized intervention motions for high-risk cohorts',
      'Direct linkage to wellbeing strategy, manager training, and retention plans',
    ],
  },
  {
    icon: Hospital,
    title: 'EAP and benefits providers',
    useCase: 'Move from reactive crisis support to proactive prevention that increases utilization quality and member trust.',
    outcomes: [
      'Differentiated benefit offering with measurable engagement proof',
      'Escalation pathways into licensed care when risk exceeds threshold',
      'API-ready referral and care handoff operations',
    ],
  },
  {
    icon: UserRoundCog,
    title: 'Clinical and coaching organizations',
    useCase: 'Start sessions with context-rich trend intelligence instead of fragmented recollection, increasing intervention precision.',
    outcomes: [
      'Shorter intake time and stronger clinical continuity',
      'Pattern-led coaching plans vs anecdote-only narratives',
      'Program-level outcomes without exposing private user content',
    ],
  },
];

export default function EnterpriseSolutions() {
  useSEO({
    title: 'Enterprise Solutions | TENSION for Work',
    description:
      'Explore TENSION Enterprise solutions for HR teams, EAP providers, and clinical partners with relationship wellness intelligence.',
    canonical: '/enterprise/solutions',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    keywords: [
      'enterprise relationship wellness',
      'relationship intelligence for hr',
      'eap relationship support',
      'workplace relationship wellbeing',
    ],
  });

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-6xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay bg-[radial-gradient(circle_at_10%_8%,rgba(124,58,237,0.18),transparent_38%),radial-gradient(circle_at_88%_0%,rgba(217,70,239,0.16),transparent_34%)]" />
          <div className="enterprise-hero-content">
          <img
            src="/branding/tension-enterprise-logo.svg"
            alt="TENSION Enterprise"
            className="mb-4 h-auto w-full max-w-md rounded-xl border border-slate-700/70 bg-slate-950/70 p-1"
          />
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            Enterprise solutions
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">High-impact value by buyer type</h1>
          <p className="mt-3 max-w-3xl text-slate-700 md:text-lg">
            TENSION for Work is built to deliver measurable, procurement-ready outcomes for each stakeholder group responsible
            for wellbeing, retention, and care quality.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-violet-200/80 bg-white/85 px-3 py-1 text-xs text-violet-700">
            <Building2 className="h-3.5 w-3.5" aria-hidden />
            Buyer-specific pathways with privacy-first analytics
          </div>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          {solutions.map((solution) => (
            <Card key={solution.title} className="enterprise-surface-card enterprise-lift-card group p-6">
              <div className="enterprise-icon-chip">
                <solution.icon className="h-8 w-8 text-blue-300" aria-hidden />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-slate-100">{solution.title}</h2>
              <p className="mt-2 text-sm text-slate-300">{solution.useCase}</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                {solution.outcomes.map((item) => (
                  <li key={item} className="flex gap-2">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        <section className="enterprise-surface-card rounded-2xl border-fuchsia-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-100 md:text-2xl">
            <Sparkles className="h-5 w-5 text-fuchsia-300" aria-hidden />
            Pilot design concept
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300 md:text-base">
            Pilots combine anonymized dashboards, role-scoped controls, and consent-led data boundaries so buyers can evaluate
            business impact quickly while maintaining user trust and privacy discipline.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/enterprise/pilot">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95">
                View pilot framework
              </Button>
            </Link>
            <Link to="/business-inquiries?inquiryType=enterprise">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Contact sales
              </Button>
            </Link>
            <Link to="/enterprise/security">
              <Button variant="ghost" className="text-slate-700 hover:text-slate-900">
                Security posture
              </Button>
            </Link>
          </div>
        </section>

        <EnterpriseProofPack compact />

        <div className="flex items-center justify-between gap-3 border-t border-violet-100 pt-4 text-sm">
          <Link to="/enterprise" className="text-violet-700 hover:underline">
            Back to Enterprise
          </Link>
          <HeartHandshake className="h-4 w-4 text-violet-500" aria-hidden />
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
